/**
 * One API surface for the member profile in both modes:
 *
 *  - self  → api/members/me/* (the signed-in user; PUT /me is partial, null clears a field)
 *  - admin → api/members/{memberId}/* (keyed by MemberID so visitors and children without a
 *            login work too)
 *
 * Tabs call `api.x.list()` etc. and use `api.keys` for react-query, so the header and the tabs
 * share one cache entry per resource.
 */
import { useMemo } from 'react';
import {
  useMemberService,
  useMemberMeService,
  type MemberAddress,
  type MemberEmergencyContact,
  type MemberEmergencyContactRequest,
  type MemberFamilyView,
  type MemberLocationView,
  type UpdateMemberLocationRequest,
  type UpdateMemberRequest,
  type MyAllergyView,
  type MyDisabilityView,
} from '@actschurch/ccms-sdk';
import type { MemberRecord } from './memberFormat';
import { errorStatus } from './memberFormat';

export type MemberProfileMode = 'self' | 'admin';

export interface MemberAddressFields {
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  city?: string;
  code?: string;
  province?: string;
  country?: string;
}

interface SubResource<TRow, TAdd, TUpdate, TKey = number> {
  list: () => Promise<TRow[]>;
  add: (data: TAdd) => Promise<unknown>;
  update: (id: TKey, data: TUpdate) => Promise<unknown>;
  remove: (id: TKey) => Promise<unknown>;
}

export interface MemberProfileApi {
  mode: MemberProfileMode;
  /** Root of every query key for this profile. */
  scope: readonly unknown[];
  keys: {
    member: readonly unknown[];
    addresses: readonly unknown[];
    allergies: readonly unknown[];
    disabilities: readonly unknown[];
    emergency: readonly unknown[];
    families: readonly unknown[];
    locations: readonly unknown[];
    notes: readonly unknown[];
    audit: readonly unknown[];
    /** Contact list (admin mode: api/members/{id}/contact-points) */
    contactPoints: readonly unknown[];
    /** Custom field values (admin mode: api/members/{id}/custom-fields) */
    customFields: readonly unknown[];
  };
  /** Self: GET /me (null when the login has no member row). Admin: GET /view/{id}. */
  getMember: () => Promise<MemberRecord | null>;
  updateMember: (patch: UpdateMemberRequest) => Promise<unknown>;
  addresses: SubResource<MemberAddress, MemberAddressFields, MemberAddressFields>;
  allergies: SubResource<MyAllergyView, { allergyID: number; note?: string | null }, { note?: string | null }>;
  disabilities: SubResource<MyDisabilityView, { disabilityID: number; note?: string | null }, { note?: string | null }>;
  emergency: SubResource<MemberEmergencyContact, MemberEmergencyContactRequest, MemberEmergencyContactRequest>;
  getFamilies: () => Promise<MemberFamilyView[]>;
  getLocations: () => Promise<MemberLocationView[]>;
  setLocation: (data: UpdateMemberLocationRequest) => Promise<unknown>;
}

/** Query key root used by the completion banner and /profile (self mode). */
export const MY_PROFILE_STATUS_KEY = ['members', 'me', 'profile-status'] as const;

/** Self-mode fields PUT /me accepts; everything else is ignored server-side, so never send it. */
const SELF_FIELDS: (keyof UpdateMemberRequest)[] = [
  'firstName', 'middleName', 'lastName', 'dateOfBirth', 'gender', 'maritalStatusID',
  'cellNumber', 'altContact', 'telephone', 'avatar', 'hideBirthdayFromColleagues',
];

export function useMemberProfileApi(mode: MemberProfileMode, memberId?: number | null): MemberProfileApi {
  const memberService = useMemberService();
  const meService = useMemberMeService();

  return useMemo<MemberProfileApi>(() => {
    const scope = ['members', 'profile', mode === 'self' ? 'me' : memberId ?? 'none'] as const;
    const keys = {
      member: [...scope, 'member'],
      addresses: [...scope, 'addresses'],
      allergies: [...scope, 'allergies'],
      disabilities: [...scope, 'disabilities'],
      emergency: [...scope, 'emergency'],
      families: [...scope, 'families'],
      locations: [...scope, 'locations'],
      notes: [...scope, 'notes'],
      audit: [...scope, 'audit'],
      contactPoints: [...scope, 'contact-points'],
      customFields: [...scope, 'custom-fields'],
    };

    if (mode === 'self') {
      return {
        mode,
        scope,
        keys,
        getMember: async () => {
          try {
            return (await meService.getMe()) as MemberRecord | null;
          } catch (e) {
            if (errorStatus(e) === 404) return null;
            throw e;
          }
        },
        updateMember: (patch) => {
          const body: Record<string, unknown> = {};
          for (const f of SELF_FIELDS) if (f in patch) body[f] = patch[f];
          // The self DTO types hideBirthdayFromColleagues as boolean (no null).
          return meService.updateMe(body as Parameters<typeof meService.updateMe>[0]);
        },
        addresses: {
          list: () => meService.getMyAddresses(),
          add: (d) => meService.addMyAddress(d),
          update: (id, d) => meService.updateMyAddress(id, d),
          remove: (id) => meService.deleteMyAddress(id),
        },
        allergies: {
          list: () => meService.getMyAllergies(),
          add: (d) => meService.addMyAllergy(d),
          update: (id, d) => meService.updateMyAllergy(id, d),
          remove: (id) => meService.deleteMyAllergy(id),
        },
        disabilities: {
          list: () => meService.getMyDisabilities(),
          add: (d) => meService.addMyDisability(d),
          update: (id, d) => meService.updateMyDisability(id, d),
          remove: (id) => meService.deleteMyDisability(id),
        },
        emergency: {
          list: () => meService.getMyEmergencyContacts(),
          add: (d) => meService.addMyEmergencyContact(d),
          update: (id, d) => meService.updateMyEmergencyContact(id, d),
          remove: (id) => meService.deleteMyEmergencyContact(id),
        },
        getFamilies: () => meService.getMyFamily(),
        getLocations: () => meService.getMyLocations(),
        setLocation: (d) => meService.updateMyLocations(d),
      };
    }

    const id = memberId ?? 0;
    return {
      mode,
      scope,
      keys,
      getMember: async () => {
        if (!id) return null;
        return (await memberService.getViewById(id)) as unknown as MemberRecord;
      },
      updateMember: (patch) => memberService.update(id, patch),
      addresses: {
        list: () => memberService.getMemberAddresses(id),
        add: (d) => memberService.addMemberAddress(id, d),
        update: (addressId, d) => memberService.updateMemberAddress(id, addressId, d),
        remove: (addressId) => memberService.deleteMemberAddress(id, addressId),
      },
      allergies: {
        list: () => memberService.getMemberAllergies(id),
        add: (d) => memberService.addMemberAllergy(id, d),
        update: (allergyId, d) => memberService.updateMemberAllergy(id, allergyId, d),
        remove: (allergyId) => memberService.deleteMemberAllergy(id, allergyId),
      },
      disabilities: {
        list: () => memberService.getMemberDisabilities(id),
        add: (d) => memberService.addMemberDisability(id, d),
        update: (disabilityId, d) => memberService.updateMemberDisability(id, disabilityId, d),
        remove: (disabilityId) => memberService.deleteMemberDisability(id, disabilityId),
      },
      emergency: {
        list: () => memberService.getEmergencyContacts(id),
        add: (d) => memberService.addEmergencyContact(id, d),
        update: (contactId, d) => memberService.updateEmergencyContact(id, contactId, d),
        remove: (contactId) => memberService.deleteEmergencyContact(id, contactId),
      },
      getFamilies: () => memberService.getMemberFamilies(id),
      getLocations: () => memberService.getMemberLocations(id),
      setLocation: (d) => memberService.setMemberLocation(id, d),
    };
  }, [mode, memberId, memberService, meService]);
}
